import React, { useEffect, useMemo, useState } from "react";
import {
  RotateCw,
  Search,
  History,
  AlertCircle,
  CheckCircle2,
  XCircle,
  MinusCircle,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  X,
  RefreshCw,
  FileText,
} from "lucide-react";
import {
  listHatSchedules,
  getAllHatRuns,
  HatRunRecord,
  HatRunOutcome,
  ScheduledHatSummary,
} from "../../../service/apiService";

interface HatOption {
  id: string;
  hat_name: string;
  file_name?: string;
}

type DatePreset = "any" | "24h" | "7d" | "30d" | "custom";
type OutcomeFilter = "all" | HatRunOutcome;

// Records shown per local page. Pagination is client-side over the loaded
// set — the backend returns everything (up to its own hard cap) in one go.
const PAGE_SIZE = 25;

const SchedulerRunHistoryDashboard: React.FC = () => {
  // ── Hat selector (only VAs with a scheduler) ──
  const [hats, setHats] = useState<HatOption[]>([]);
  const [hatsLoading, setHatsLoading] = useState(false);
  const [hatQuery, setHatQuery] = useState("");
  const [hatDropdownOpen, setHatDropdownOpen] = useState(false);
  const [selectedHatId, setSelectedHatId] = useState<string | null>(null);

  // ── Date range ── default "Any" so the dashboard shows the full retained window on mount
  const [datePreset, setDatePreset] = useState<DatePreset>("any");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // ── Outcome filter (client-side, applied to loaded page) ──
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>("all");

  // ── Run data ──
  const [runs, setRuns] = useState<HatRunRecord[] | null>(null);
  const [retentionDays, setRetentionDays] = useState<number>(30);
  // loading starts true: the first render runs before the auto-load
  // useEffect, so `runs` is briefly null. Without this, the table branch
  // would dereference null.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // hat_id → display name for the VA Name column. Falls back to the id.
  const hatNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const h of hats) map[h.id] = h.hat_name;
    return map;
  }, [hats]);

  // hat_id → monitored file name (from the schedule's task_config).
  const fileNameByVaId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const h of hats) if (h.file_name) map[h.id] = h.file_name;
    return map;
  }, [hats]);

  const selectedHat = useMemo(
    () => hats.find(h => h.id === selectedHatId) || null,
    [hats, selectedHatId]
  );

  // ── Load scheduled-hat list once (admin dropdown) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setHatsLoading(true);
      try {
        const result: ScheduledHatSummary[] = await listHatSchedules();
        if (cancelled) return;
        const list: HatOption[] = (result || [])
          .map(s => ({
            id: s.va_id,
            hat_name: s.hat_name || s.va_id,
            file_name: s.task_config?.file_name,
          }))
          .sort((a, b) => a.hat_name.localeCompare(b.hat_name));
        setHats(list);
      } catch (err) {
        console.error("[SchedulerRunHistory] Failed to load schedules:", err);
      } finally {
        if (!cancelled) setHatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply preset → fill from/to (or clear them on "any")
  useEffect(() => {
    if (datePreset === "custom") return;
    if (datePreset === "any") {
      setDateFrom("");
      setDateTo("");
      return;
    }
    const now = new Date();
    const from = new Date(now);
    if (datePreset === "24h") from.setDate(from.getDate() - 1);
    if (datePreset === "7d") from.setDate(from.getDate() - 7);
    if (datePreset === "30d") from.setDate(from.getDate() - 30);
    setDateFrom(toLocalInputValue(from));
    setDateTo(toLocalInputValue(now));
  }, [datePreset]);

  // ── Auto-load on mount: show full history (no VA filter, no date filter) ──
  useEffect(() => {
    fetchRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredHats = useMemo(() => {
    const q = hatQuery.trim().toLowerCase();
    if (!q) return hats;
    return hats.filter(h => h.hat_name.toLowerCase().includes(q));
  }, [hats, hatQuery]);

  const visibleRuns = useMemo(() => {
    if (!runs) return null;
    if (outcomeFilter === "all") return runs;
    return runs.filter(r => r.outcome === outcomeFilter);
  }, [runs, outcomeFilter]);

  const totalPages = useMemo(() => {
    if (!visibleRuns || visibleRuns.length === 0) return 1;
    return Math.max(1, Math.ceil(visibleRuns.length / PAGE_SIZE));
  }, [visibleRuns]);

  const pagedRuns = useMemo(() => {
    if (!visibleRuns) return [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return visibleRuns.slice(start, start + PAGE_SIZE);
  }, [visibleRuns, currentPage]);

  // Reset to page 1 whenever the filtered set changes shape (new fetch or
  // outcome filter toggle). Also clamp if the user navigates to a page
  // beyond the new total.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [outcomeFilter]);

  const counts = useMemo(() => {
    const base = { total: 0, success: 0, failure: 0, no_action: 0 };
    if (!runs) return base;
    base.total = runs.length;
    for (const r of runs) {
      if (r.outcome === "success") base.success += 1;
      else if (r.outcome === "failure") base.failure += 1;
      else if (r.outcome === "no_action") base.no_action += 1;
    }
    return base;
  }, [runs]);

  const hasFilters =
    selectedHatId !== null || dateFrom !== "" || dateTo !== "";

  const handleApply = async () => {
    await fetchRuns();
  };

  const handleClearFilters = async () => {
    setSelectedHatId(null);
    setDatePreset("any");
    setDateFrom("");
    setDateTo("");
    setOutcomeFilter("all");
    await fetchRuns({ hatId: null, from: "", to: "" });
  };

  const fetchRuns = async (override?: {
    hatId?: string | null;
    from?: string;
    to?: string;
  }) => {
    setLoading(true);
    setExpandedRunId(null);
    setCurrentPage(1);
    setError(null);
    try {
      const effectiveHatId =
        override && "hatId" in override ? override.hatId : selectedHatId;
      const effectiveFrom =
        override && "from" in override ? override.from! : dateFrom;
      const effectiveTo = override && "to" in override ? override.to! : dateTo;

      const resp = await getAllHatRuns({
        hatId: effectiveHatId || undefined,
        from: effectiveFrom ? toIsoUtc(effectiveFrom) : undefined,
        to: effectiveTo ? toIsoUtc(effectiveTo) : undefined,
      });
      setRetentionDays(resp.retention_days);
      setRuns(resp.runs);
    } catch (err: any) {
      console.error("[SchedulerRunHistory] fetch failed:", err);
      const detail =
        err?.response?.data?.detail || err?.message || "Unknown error";
      setError(`Failed to load run history: ${detail}`);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* ── Filters card ───────────────────────────────────────── */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-4">
        {/* Row 1: VA selector + retention badge */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Virtual Assistant{" "}
              <span className="text-gray-400 font-normal">
                (optional — leave empty to see all)
              </span>
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setHatDropdownOpen(o => !o)}
                className="w-full md:w-96 inline-flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
              >
                <span className="truncate">
                  {selectedHat
                    ? selectedHat.hat_name
                    : hatsLoading
                      ? "Loading scheduled VAs..."
                      : "All scheduled Virtual Assistants"}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
              </button>
              {hatDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full md:w-96 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                      <input
                        autoFocus
                        type="text"
                        value={hatQuery}
                        onChange={e => setHatQuery(e.target.value)}
                        placeholder="Search scheduled VAs..."
                        className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedHatId(null);
                        setHatDropdownOpen(false);
                        setHatQuery("");
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-violet-50 transition-colors ${
                        selectedHatId === null
                          ? "bg-violet-50 text-violet-700"
                          : "text-gray-800"
                      }`}
                    >
                      <div className="font-medium">
                        All scheduled VAs
                      </div>
                      <div className="text-xs text-gray-500">
                        Show runs from every scheduled VA
                      </div>
                    </button>
                    {filteredHats.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-500 text-center">
                        {hatsLoading
                          ? "Loading..."
                          : hats.length === 0
                            ? "No scheduled VAs yet"
                            : "No VAs match"}
                      </div>
                    ) : (
                      filteredHats.map(h => (
                        <button
                          key={h.id}
                          type="button"
                          onClick={() => {
                            setSelectedHatId(h.id);
                            setHatDropdownOpen(false);
                            setHatQuery("");
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-violet-50 transition-colors ${
                            selectedHatId === h.id
                              ? "bg-violet-50 text-violet-700"
                              : "text-gray-800"
                          }`}
                        >
                          <div className="font-medium truncate">
                            {h.hat_name}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-violet-50 border border-violet-100 rounded-md px-3 py-2">
            <CalendarDays className="h-4 w-4 text-violet-600" />
            <span>
              Records retained for{" "}
              <span className="font-semibold text-violet-700">
                {retentionDays} days
              </span>
            </span>
          </div>
        </div>

        {/* Row 2: Date presets */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Date range
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {(["any", "24h", "7d", "30d", "custom"] as DatePreset[]).map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setDatePreset(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  datePreset === p
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {p === "any"
                  ? "Any"
                  : p === "24h"
                    ? "Last 24h"
                    : p === "7d"
                      ? "Last 7 days"
                      : p === "30d"
                        ? "Last 30 days"
                        : "Custom"}
              </button>
            ))}
            <input
              type="datetime-local"
              value={dateFrom}
              onChange={e => {
                setDateFrom(e.target.value);
                setDatePreset("custom");
              }}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            <span className="text-xs text-gray-500">to</span>
            <input
              type="datetime-local"
              value={dateTo}
              onChange={e => {
                setDateTo(e.target.value);
                setDatePreset("custom");
              }}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>
        </div>

        {/* Row 3: Outcome filter + Apply */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-600">Outcome:</span>
            {(
              [
                { v: "all", label: "All" },
                { v: "success", label: "Success" },
                { v: "failure", label: "Failure" },
                { v: "no_action", label: "No action" },
              ] as { v: OutcomeFilter; label: string }[]
            ).map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setOutcomeFilter(opt.v)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  outcomeFilter === opt.v
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {hasFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                disabled={loading}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
                Clear filters
              </button>
            )}
            <button
              type="button"
              onClick={handleApply}
              disabled={loading}
              title="Reload with the current filters"
              className="inline-flex justify-center items-center px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`-ml-1 mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={loading}
              className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:bg-violet-300 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="-ml-1 mr-2 h-4 w-4" />
                  Apply
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Error banner ───────────────────────────────────────── */}
      {error && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-md">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* ── Summary stats ──────────────────────────────────────── */}
      {runs && runs.length > 0 && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total" value={counts.total} tone="violet" />
          <StatCard label="Success" value={counts.success} tone="emerald" />
          <StatCard label="Failure" value={counts.failure} tone="red" />
          <StatCard label="No action" value={counts.no_action} tone="gray" />
        </div>
      )}

      {/* ── Body ───────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-16 bg-gray-50 rounded-lg">
          <RotateCw className="animate-spin h-5 w-5 text-violet-500 mr-2" />
          <span className="text-violet-500 text-sm">Loading run history...</span>
        </div>
      ) : !visibleRuns || visibleRuns.length === 0 ? (
        <EmptyState
          icon={<History className="w-10 h-10 text-gray-300 mb-3" />}
          title={
            hasFilters
              ? "No runs match the current filters"
              : "No scheduled runs recorded yet"
          }
          subtitle={
            outcomeFilter !== "all" && runs && runs.length > 0
              ? `No ${prettyOutcome(outcomeFilter)} runs in the loaded window. Try a different outcome filter.`
              : hasFilters
                ? "Try widening the date range, picking a different VA, or clear filters."
                : "Run history will appear here once a scheduled VA executes."
          }
        />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-8 px-2 py-2.5"></th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    VA Name
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    File
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Started
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Outcome
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Attempt
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedRuns.map(run => {
                  const expanded = expandedRunId === run.run_id;
                  const hasDetails =
                    run.details && Object.keys(run.details).length > 0;
                  const vaName =
                    hatNameById[run.hat_id] ||
                    (run.details && (run.details as any).va_name) ||
                    (run.details && (run.details as any).hat_name) ||
                    run.hat_id;
                  const fileName =
                    fileNameByVaId[run.hat_id] ||
                    (run.details && (run.details as any).file_name) ||
                    null;
                  return (
                    <React.Fragment key={run.run_id}>
                      <tr
                        className={`hover:bg-gray-50/60 transition-colors ${
                          hasDetails ? "cursor-pointer" : ""
                        }`}
                        onClick={() =>
                          hasDetails &&
                          setExpandedRunId(expanded ? null : run.run_id)
                        }
                      >
                        <td className="px-2 py-3 text-gray-400">
                          {hasDetails ? (
                            expanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900 truncate max-w-[200px] block">
                            {vaName}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {fileName ? (
                            <div className="flex items-center gap-2 text-gray-700 max-w-[240px]">
                              <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="truncate" title={fileName}>
                                {fileName}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {formatDateTime(
                            run.started_at || run.finished_at || null
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <OutcomePill outcome={run.outcome} />
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {run.attempt}
                        </td>
                        <td className="px-4 py-3 max-w-[360px]">
                          {run.outcome === "failure" && run.error_message ? (
                            <p
                              className="text-red-600 truncate"
                              title={run.error_message}
                            >
                              {run.error_message}
                            </p>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                      {expanded && hasDetails && (
                        <tr className="bg-gray-50/70">
                          <td colSpan={7} className="px-6 py-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">
                              Run details
                            </p>
                            <pre className="text-xs text-gray-700 bg-white border border-gray-200 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                              {JSON.stringify(run.details, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer: count + Previous / Page X of Y / Next */}
          <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-500">
              {(() => {
                const total = (visibleRuns ?? []).length;
                if (total === 0) return "0 runs";
                const start = (currentPage - 1) * PAGE_SIZE + 1;
                const end = Math.min(currentPage * PAGE_SIZE, total);
                return (
                  <>
                    Showing{" "}
                    <span className="font-medium text-gray-700">
                      {start}–{end}
                    </span>{" "}
                    of {total} run{total === 1 ? "" : "s"}
                  </>
                );
              })()}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-600">
                  Page <span className="font-medium text-gray-800">{currentPage}</span> of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 disabled:cursor-not-allowed"
                >
                  Next Page
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: number;
  tone: "violet" | "emerald" | "red" | "gray";
}> = ({ label, value, tone }) => {
  const tones: Record<string, string> = {
    violet: "bg-violet-50 text-violet-700",
    emerald: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
    gray: "bg-gray-50 text-gray-700",
  };
  return (
    <div className={`p-4 rounded-lg ${tones[tone]}`}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value.toLocaleString()}</p>
    </div>
  );
};

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}> = ({ icon, title, subtitle }) => (
  <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
    {icon}
    <p className="text-gray-600 text-sm font-medium">{title}</p>
    <p className="text-gray-400 text-xs mt-1 text-center max-w-md px-4">
      {subtitle}
    </p>
  </div>
);

const OutcomePill: React.FC<{ outcome: HatRunOutcome | string }> = ({
  outcome,
}) => {
  if (outcome === "success") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="h-3 w-3" />
        Success
      </span>
    );
  }
  if (outcome === "failure") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
        <XCircle className="h-3 w-3" />
        Failure
      </span>
    );
  }
  if (outcome === "no_action") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
        <MinusCircle className="h-3 w-3" />
        No action
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
      {outcome || "Unknown"}
    </span>
  );
};

function prettyOutcome(o: OutcomeFilter): string {
  if (o === "success") return "successful";
  if (o === "failure") return "failed";
  if (o === "no_action") return "no-action";
  return "";
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toIsoUtc(localValue: string): string {
  if (!localValue) return "";
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return localValue;
  return d.toISOString();
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default SchedulerRunHistoryDashboard;
